import DataGrid from '@/components/dataGrid/dataGrid';
import ShowHide from '@/components/showHide';
import Tooltip from '@/components/tooltip';
import { Company } from '@/modules/company/entities/interfaces/company';
import { DialogConfirmDeleteHealthDataConfig } from '@/modules/companyHealthDataConfig/components/dialogConfirmDeleteHealthDataConfig';
import { DialogSaveChangesBeforeLeaveThePage } from '@/modules/companyHealthDataConfig/components/dialogSaveChangesBeforeLeaveThePage';
import { HEALTH_DATA_STEPS } from '@/modules/companyHealthDataConfig/entities/enums/healthData.enums';
import { ListParams } from '@/modules/companyHealthDataConfig/entities/interfaces/healthData.interfaces';
import { useHealthDataRequests } from '@/modules/companyHealthDataConfig/hooks/useHealthDataRequests';
import { useHealthDataSelectedCompanyIds } from '@/modules/companyHealthDataConfig/hooks/useHealthDataSelectedCompanyIds';
import { useSaveChangeBeforeLeaveThePage } from '@/modules/companyHealthDataConfig/hooks/useSaveChangeBeforeLeaveThePage';
import { LIST_INITIAL_PAGE, LIST_INITIAL_PAGE_SIZE } from '@/shared/entities/constants/list';
import { useQueryStringState } from '@/shared/hooks/useQueryStringState';
import ICONS from '@/shared/entities/constants/icons';
import { checkPermission, PERMISSIONS } from '@/utils/checkPermission';
import formatText from '@/utils/formatText';
import React, { useState } from 'react';
import { Button, Checkbox, Chip, Tabs, Text } from 'vkit/lib/components';
import { Grid } from 'vkit/lib/context';

export const List: React.FC = () => {
  const hasPermissionToManageHealthData = checkPermission(PERMISSIONS.COMPANY_HEALTH_DATA_MANAGE);

  const [listParams, setListParams] = useQueryStringState<ListParams>({
    stepHealthData: HEALTH_DATA_STEPS.DISABLED,
  });

  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);

  const {
    selectedCompanyIdsByStep,
    setSelectedCompanyIdsByStep,
    companyIdsOnPageByStep,
    setCompanyIdsOnPageByStep,
    selectedCompanyIdsOnPage,
    updateCompanyIds,
  } = useHealthDataSelectedCompanyIds({
    stepHealthData: listParams.stepHealthData,
  });

  const { saving, deleting, saveManyCompanyConfigs, deleteManyCompanyConfigs, getPaginatedList } =
    useHealthDataRequests({
      requestCallback: () => {
        setSelectedCompanyIdsByStep((current) => ({
          ...current,
          [listParams.stepHealthData]: [],
        }));
        setListParams((current) => ({
          ...current,
          page: 1,
        }));
      },
    });

  const { openConfirmationDialog, answerConfirmation } = useSaveChangeBeforeLeaveThePage({
    shouldConfirm: selectedCompanyIdsByStep[listParams.stepHealthData].length > 0,
    onConfirmSaveChanges: async () => {
      listParams.stepHealthData === HEALTH_DATA_STEPS.ENABLED
        ? await deleteManyCompanyConfigs(selectedCompanyIdsByStep[HEALTH_DATA_STEPS.ENABLED])
        : await saveManyCompanyConfigs(selectedCompanyIdsByStep[HEALTH_DATA_STEPS.DISABLED]);
    },
  });

  return (
    <>
      <DialogConfirmDeleteHealthDataConfig
        open={openConfirmDeleteDialog}
        setOpen={setOpenConfirmDeleteDialog}
        confirm={async (confirmation) => {
          if (confirmation) {
            await deleteManyCompanyConfigs(selectedCompanyIdsByStep[listParams.stepHealthData]);
          }

          setOpenConfirmDeleteDialog(false);
        }}
      />

      <DialogSaveChangesBeforeLeaveThePage
        open={Boolean(openConfirmationDialog)}
        confirm={(response) => answerConfirmation?.(response)}
      />

      <Grid column gap={16} stretch>
        <Tabs
          elements={[]}
          actions={[
            {
              label: 'Desabilitadas',
              active: listParams.stepHealthData == HEALTH_DATA_STEPS.DISABLED,
            },
            {
              label: 'Habilitadas',
              active: listParams.stepHealthData == HEALTH_DATA_STEPS.ENABLED,
            },
          ]}
          onChange={(stepNumber) => {
            setListParams({
              stepHealthData:
                stepNumber === 0 ? HEALTH_DATA_STEPS.DISABLED : HEALTH_DATA_STEPS.ENABLED,
              pageSize: LIST_INITIAL_PAGE_SIZE,
              page: LIST_INITIAL_PAGE,
            });
          }}
        />

        <ShowHide transition='slideToDown' visible={!hasPermissionToManageHealthData}>
          <Text
            value='Você não tem permissão para gerenciar dados de saúde.'
            color='warning'
            icon={ICONS.WARNING}
            iconColor='warning'
            applyBaseColor
            rounded
            padding='8px 12px'
            size='medium'
          />
        </ShowHide>

        <DataGrid
          key={listParams.stepHealthData}
          filterStateController={[listParams, setListParams]}
          source={async (filters) => {
            const response = await getPaginatedList(filters);
            setCompanyIdsOnPageByStep((current) => ({
              ...current,
              [listParams.stepHealthData]: response.data.map((company) => company.id),
            }));

            return response;
          }}
          filters={[
            {
              label: 'Buscar grupo empresarial',
              key: 'parentCompanyTerm',
              width: 50,
            },
            {
              label: 'Buscar empresa',
              key: 'q',
              width: 50,
            },
          ]}
          columns={[
            {
              headerName: (
                <Grid alignItems='center'>
                  <Tooltip
                    title={
                      !hasPermissionToManageHealthData
                        ? 'Você não tem permissão para gerenciar dados de saúde.'
                        : undefined
                    }>
                    <Checkbox
                      small
                      disabled={
                        saving ||
                        deleting ||
                        companyIdsOnPageByStep[listParams.stepHealthData].length === 0 ||
                        !hasPermissionToManageHealthData
                      }
                      indeterminate={
                        selectedCompanyIdsOnPage.length > 0 &&
                        selectedCompanyIdsOnPage.length <
                          companyIdsOnPageByStep[listParams.stepHealthData].length
                      }
                      checked={
                        selectedCompanyIdsOnPage.length ===
                          companyIdsOnPageByStep[listParams.stepHealthData].length &&
                        companyIdsOnPageByStep[listParams.stepHealthData].length > 0
                      }
                      onChange={(checked: boolean) => {
                        updateCompanyIds(
                          checked,
                          listParams.stepHealthData,
                          companyIdsOnPageByStep[listParams.stepHealthData],
                        );
                      }}
                    />
                  </Tooltip>
                </Grid>
              ),
              render: ({ id }: Company) => (
                <Tooltip
                  title={
                    !hasPermissionToManageHealthData
                      ? 'Você não tem permissão para gerenciar dados de saúde.'
                      : undefined
                  }>
                  <Checkbox
                    small
                    disabled={saving || deleting || !hasPermissionToManageHealthData}
                    checked={selectedCompanyIdsOnPage.includes(id)}
                    onChange={(checked: boolean) => {
                      updateCompanyIds(checked, listParams.stepHealthData, [id]);
                    }}
                  />
                </Tooltip>
              ),
            },
            {
              headerName: 'Grupo empresarial',
              render: ({ parent, name }: Company) => (
                <Text size='medium' value={parent?.name || name} />
              ),
              sub: {
                render: ({ parent, cnpj }: Company) => (
                  <Text value={formatText(parent?.cnpj || cnpj, 'cnpj')} />
                ),
              },
            },
            {
              headerName: 'Empresa',
              field: 'name',
              sub: {
                field: 'cnpj',
                formatData: 'cnpj',
              },
            },
            {
              headerName: 'Dados de saúde',
              render: () => (
                <Chip
                  rounded
                  size='small'
                  color='light'
                  label={
                    listParams.stepHealthData === HEALTH_DATA_STEPS.ENABLED
                      ? 'Habilitado'
                      : 'Desabilitado'
                  }
                />
              ),
            },
          ]}
        />

        <Grid stretch alignContent='right'>
          <Button
            label={
              listParams.stepHealthData === HEALTH_DATA_STEPS.DISABLED
                ? 'Habilitar dados de saúde'
                : 'Dasabilitar dados de saúde'
            }
            disabled={
              selectedCompanyIdsByStep[listParams.stepHealthData].length === 0 || saving || deleting
            }
            loading={saving || deleting}
            onClick={async () => {
              listParams.stepHealthData === HEALTH_DATA_STEPS.ENABLED
                ? setOpenConfirmDeleteDialog(true)
                : await saveManyCompanyConfigs(selectedCompanyIdsByStep[listParams.stepHealthData]);
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
